import {
  COUNTRY_OPTIONS,
  getStateOptions,
  isStateRequired,
} from '@/utils/countryRegions';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Checkbox, Divider, Form, Spin } from 'antd';
import type { FormInstance } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useSelector } from 'react-redux';

import {
  CustomDatePicker,
  CustomInput,
  CustomSelect,
  CustomTitle,
} from '../../../components';
import TeamDropdownSelect, {
  TeamDropdownSelectTeam,
} from '../../../components/TeamDropdownSelect';
import { useToken } from '../../../common/helper';
import { getTeamsDDLOFFeature } from '../../../common/utils/dropDown';
import { usePaginatedProductDropdown } from '../../../hooks/usePaginatedProductDropdown';
import type { SelectedProduct } from '../components/ProductMultiSelectField';
import {
  BookFedExShipment,
  CompanyCourierAccount,
  EmailFedExLabel,
  type EmailLabelItem,
  FedExQuotePayload,
  FedExRate,
  FedExShipPayload,
  FedExShipResponseData,
  GetFedExAccounts,
  parseFedExQuoteResponse,
  parseFedExShipResponse,
  RequestFedExQuote,
} from '../../../apis/fedexQuote';
import { formatQuoteApiError } from './formatQuoteApiError';
import {
  attemptBooking,
  bookingFailureMessage,
  type BookingFailure,
  type BookingKeyRef,
} from './bookingGuard';
import BookActions from './BookActions';
import LabelCards from './LabelCards';
import { renderOnce, renderZplToPng } from './labelary';
import { createSerialQueue } from './serialQueue';
import { findSavedBooking } from './savedBooking';
import {
  bookedLabels,
  buildPrintHtml,
  concatZpl,
  labelCountWarning,
  isImageContentType,
  isZplContentType,
  type BookedLabel,
} from './labels';
import PackageLinesFields, {
  PackageTotals,
  type LineProductsSlot,
  SCROLL_TO_FIELD,
  type PackageLinesFieldsHandle,
} from './PackageLinesFields';
import LineProductsFields from './LineProductsFields';
import {
  buildCreatePieces,
  diffAgainstOutbound,
  hasCrossBoxDuplicateSerial,
  outboundPrefill,
  serialBoxes,
  serialWarning as serialClashWarning,
  summedProductsPayload,
} from './boxProducts';
import {
  LINE_WEIGHT_DEPENDENCIES,
  boxesFromLines,
  buildPieces,
  dimsPayload,
  emptyLine,
  expandLineProducts,
  lineQty,
  minBoxWeightLb,
  pieceOffset,
  positiveFiniteNumber,
  productsForKey,
  singleBox,
  totalBoxes,
  totalWeightLb,
  type PackageLineValues,
} from './lines';
import { useMultiPackageGate } from '../../../hooks/useMultiPackageGate';
import {
  BookUPSShipment,
  EmailUPSLabel,
  GetUPSAccounts,
  parseUPSQuoteResponse,
  parseUPSShipResponse,
  RequestUPSQuote,
  UPSQuotePayload,
  UPSShipPayload,
} from '../../../apis/upsQuote';
import {
  CreateNewShipment,
  fetchShipmentProducts,
  LookupShipmentByReference,
  PromoteShipment,
  GetSingleShipment,
  UpdateShipment,
  uploadFile,
} from '../../../apis/shipment';
import {
  FEDEX_COURIER_ID,
  UPS_COURIER_ID,
  isUPSShipment,
  nextReturnReference,
  normalizeCountryCode,
  normalizePostalCode,
  normalizeStateCode,
  isValidStateForCountry,
  outboundToReturnQuoteFormValues,
  phoneDigitsWithoutExtension,
  productsPayloadFromSelectedRows,
  quotePromoteTargetId,
  selectedProductsFromOutboundLines,
  shouldFallbackReturnProductsFetch,
  shouldShowQuoteTeamsProducts,
  shellToQuoteFormValues,
  weightLbToKg,
  type ProductsPrefillStatus,
} from '../shellShipmentHelpers';
import type {
  ShipmentProductList,
  SingleShipmentDataExtended,
} from '../../../apis/types';
import styles from './styles.module.css';
import { themeT } from '@/utils/themeText';
import theme from '@/assets/theme';

interface QuoteShipmentDrawerProps {
  onClose: () => void;
  onBack?: (visible: boolean) => void;
  /** When supplied, the drawer prefills from this shell shipment and, on
   *  successful Book & Generate Label, calls the promote endpoint to attach
   *  the FedEx courier + tracking number to the shipment. */
  shellShipment?: SingleShipmentDataExtended;
  /** Fires when a shell shipment has been promoted successfully. Used by the
   *  parent to refresh an already-open detail drawer so the Quote/Book bars
   *  disappear without the user closing and re-opening. */
  onPromoteSuccess?: () => void;
  /** ``return`` opens Generate return label: flipped parties, force-create,
   *  FedEx PRINT_RETURN_LABEL, blank billing. */
  mode?: 'quote' | 'return';
  /** Outbound shipment used only for return-mode prefill (never promoted). */
  returnSourceShipment?: SingleShipmentDataExtended;
}

interface PartyValues {
  /** Contact person name. */
  name?: string;
  /** Company / site name. */
  company?: string;
  phone?: string;
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  residential?: boolean;
}

interface FormValues {
  provider: 'FEDEX' | 'UPS';
  pickup: PartyValues;
  delivery: PartyValues;
  /** Package lines: `qty` identical boxes each. Products live in `lineProducts` state. */
  lines?: PackageLineValues[];
  shipDate?: Dayjs;
  dryIce?: boolean;
  dryIceKg?: number;
  nonStandardPackaging?: boolean;
  lithiumBatteries?: boolean;
  lithiumBatteryMaterial?: 'LITHIUM_ION' | 'LITHIUM_METAL' | 'BOTH';
  reference: string;
  comments?: string;
  commodityDescription?: string;
  commodityValue?: number;
}

type RefStatus = 'idle' | 'checking' | 'shell' | 'booked' | 'none';

type Step = 'form' | 'rates' | 'result';

/** Shared quote/ship DTO shapes — FedEx and UPS normalize to the same fields. */
type CarrierQuotePayload = FedExQuotePayload;
type CarrierRate = FedExRate;
type CarrierShipPayload = FedExShipPayload;
type CarrierShipResponse = FedExShipResponseData;

const PROVIDER_OPTIONS = [
  { value: 'FEDEX', label: 'FedEx' },
  { value: 'UPS', label: 'UPS' },
];

/** Sentinel "Bill to account" option — bill an external third-party account. */
const THIRD_PARTY_ACCOUNT = '__third_party__';

function initialProviderFromShipment(
  shipment?: SingleShipmentDataExtended | null
): FormValues['provider'] {
  // Match isUPSShipment (id or title) so drawer init agrees with return-label eligibility.
  if (isUPSShipment(shipment)) return 'UPS';
  return 'FEDEX';
}

const prettyService = (s?: string | null): string => {
  if (!s) return '';
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatPrice = (
  amount?: number | null,
  currency?: string | null
): string => {
  if (amount == null || Number.isNaN(amount)) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(Number(amount));
  } catch {
    return `${amount} ${currency || ''}`;
  }
};

/** Parse a raw FedEx delivery date/timestamp into a friendly localized string. */
const formatRateArrival = (rate: CarrierRate): string => {
  const iso = rate.deliveryDate;
  if (iso) {
    const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(iso);
    if (match) {
      const [, y, mo, d, hh, mm] = match;
      const dt = new Date(Number(y), Number(mo) - 1, Number(d));
      const dateStr = dt.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      if (hh == null) return `Arrives ${dateStr}`;
      if (hh === '23' && mm === '59') return `Arrives ${dateStr} by end of day`;
      const hour = Number(hh);
      const period = hour >= 12 ? 'PM' : 'AM';
      const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `Arrives ${dateStr} by ${h12}:${mm} ${period}`;
    }
  }
  if (rate.deliveryLabel) return `Arrives ${rate.deliveryLabel}`;
  if (rate.deliveryDay) return `Arrives ${rate.deliveryDay}`;
  return '';
};

const formatEta = (
  data: CarrierShipResponse,
  rate: CarrierRate | null
): string => {
  const fallback: CarrierRate = rate || {
    serviceType: data.serviceType || '',
    deliveryDate: data.deliveryDate ?? undefined,
    deliveryDay: data.deliveryDay ?? undefined,
  };
  const arrives = formatRateArrival(fallback);
  if (arrives) return arrives.replace(/^Arrives\s+/, '');
  if (data.transitTime) return data.transitTime;
  return '—';
};

const suffix = theme.themeType === 15 ? ' #' : '';

export const buildPayload = (values: FormValues): CarrierQuotePayload => {
  const pickupCountry = normalizeCountryCode(values.pickup?.country) || 'US';
  const deliveryCountry =
    normalizeCountryCode(values.delivery?.country) || 'US';
  const isInternational = pickupCountry !== deliveryCountry;
  const pieces = buildPieces(values);
  const box = singleBox(values);
  return {
    shipper: {
      name: values.pickup?.name?.trim(),
      company: values.pickup?.company?.trim() || undefined,
      phone: values.pickup?.phone?.trim(),
      street: values.pickup?.street?.trim(),
      street2: values.pickup?.street2?.trim() || undefined,
      city: values.pickup?.city?.trim(),
      state: normalizeStateCode(values.pickup?.state, pickupCountry),
      postalCode: normalizePostalCode(values.pickup?.postalCode, pickupCountry),
      country: pickupCountry,
      ...(values.pickup?.residential ? { residential: true } : {}),
    },
    recipient: {
      name: values.delivery?.name?.trim(),
      company: values.delivery?.company?.trim() || undefined,
      phone: values.delivery?.phone?.trim(),
      street: values.delivery?.street?.trim(),
      street2: values.delivery?.street2?.trim() || undefined,
      city: values.delivery?.city?.trim(),
      state: normalizeStateCode(values.delivery?.state, deliveryCountry),
      postalCode: normalizePostalCode(
        values.delivery?.postalCode,
        deliveryCountry
      ),
      country: deliveryCountry,
      ...(values.delivery?.residential ? { residential: true } : {}),
    },
    ...(pieces
      ? { pieces }
      : {
          weight: Number(box.weight) || 0,
          weightUnit: 'LB' as const,
          ...dimsPayload(box),
        }),
    shipDate: values.shipDate
      ? values.shipDate.format('YYYY-MM-DD')
      : undefined,
    dryIceKg: values.dryIce ? positiveFiniteNumber(values.dryIceKg) : undefined,
    nonStandardPackaging: values.nonStandardPackaging || undefined,
    lithiumBatteries: values.lithiumBatteries || undefined,
    lithiumBatteryMaterial: values.lithiumBatteries
      ? values.lithiumBatteryMaterial
      : undefined,
    reference: values.reference?.trim() || undefined,
    commodityDescription: isInternational
      ? values.commodityDescription?.trim() || 'Demo merchandise'
      : undefined,
    commodityValue: isInternational
      ? Number(values.commodityValue) || 50
      : undefined,
  };
};

/** Rewrite party fields in the form to the carrier-normalized values we send. */
const syncNormalizedPartyFields = (
  form: ReturnType<typeof Form.useForm<FormValues>>[0],
  values: FormValues
): FormValues => {
  const payload = buildPayload(values);
  const next: FormValues = {
    ...values,
    pickup: {
      ...values.pickup,
      state: payload.shipper.state,
      postalCode: payload.shipper.postalCode,
      country: payload.shipper.country,
    },
    delivery: {
      ...values.delivery,
      state: payload.recipient.state,
      postalCode: payload.recipient.postalCode,
      country: payload.recipient.country,
    },
  };
  form.setFieldsValue({
    pickup: next.pickup,
    delivery: next.delivery,
  });
  return next;
};

const blobToBase64 = async (blob: Blob): Promise<string> => {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const NO_LINES: PackageLineValues[] = [];

/** What the drawer needs from the lines: keys, box counts and serials. Weight, dims and references stay out, so typing them does not re-render the drawer (useWatch re-renders only when the selected value's JSON changes). */
const lineShapes = (values: FormValues): PackageLineValues[] =>
  (values.lines ?? NO_LINES).map((l) => ({
    key: l?.key,
    qty: l?.qty,
    boxes: l?.boxes?.map((b) => ({ serials: b?.serials })),
  }));

const withoutKey = (
  products: Record<string, SelectedProduct[]>,
  key: string
): Record<string, SelectedProduct[]> =>
  Object.fromEntries(Object.entries(products).filter(([k]) => k !== key));

/** Serials live in the box fields; the line's product list holds ids and quantities only (an outbound selection carries its serials). */
const withoutSerials = (products: SelectedProduct[]): SelectedProduct[] =>
  products.map(({ id, quantity }) => ({ id, quantity }));

type NamePath = Array<string | number>;

/** What the error-field lookup needs of a form (a FormInstance, or a stub in tests). */
export type FieldInstanceForm = Pick<FormInstance, 'getFieldInstance'>;

/** The field's element as antd's scrollToField finds it: the registered instance, else the field id (the path joined by `_`; the form has no `name`). */
const fieldNode = (form: FieldInstanceForm, name: NamePath): Element | null => {
  const instance = form.getFieldInstance(name) as
    { nativeElement?: unknown } | undefined;
  return instance?.nativeElement instanceof Element
    ? instance.nativeElement
    : document.getElementById(name.join('_'));
};

/** Errored field highest in the form (errorFields follow registration order, which a Duplicate breaks); the first registered when none is in the DOM. */
export const firstErrorFieldName = (
  form: FieldInstanceForm,
  err: unknown
): NamePath | undefined => {
  const fields =
    (err as { errorFields?: Array<{ name: NamePath }> })?.errorFields ?? [];
  let first: { name: NamePath; node: Element } | undefined;
  for (const { name } of fields) {
    const node = fieldNode(form, name);
    if (
      node &&
      (!first ||
        node.compareDocumentPosition(first.node) &
          Node.DOCUMENT_POSITION_FOLLOWING)
    ) {
      first = { name, node };
    }
  }
  return first?.name ?? fields[0]?.name;
};

/** A validation overtaken by a newer one (a field's own change check, a `dependencies` re-check, the lines re-check of errored fields after a list change) rejects with `outOfDate`, with or without errors: ask once more, unless a newer click has superseded this one (its retry would make that click's own check out of date). */
const validateAllFields = async (
  form: FormInstance<FormValues>,
  isCurrent: () => boolean
): Promise<FormValues> => {
  try {
    return await form.validateFields();
  } catch (err) {
    if (!(err as { outOfDate?: boolean })?.outOfDate || !isCurrent()) throw err;
    return form.validateFields();
  }
};

const QuoteShipmentDrawer: React.FC<QuoteShipmentDrawerProps> = ({
  onClose,
  shellShipment,
  onPromoteSuccess,
  mode = 'quote',
  returnSourceShipment,
}) => {
  const isReturnMode = mode === 'return';
  const multiBoxEnabled = useMultiPackageGate();
  const [form] = Form.useForm<FormValues>();
  const { token, decodedUserToken } = useToken();
  const teamsList = useSelector(
    (state: { teams: { list: Array<{ id?: string; name: string }> } }) =>
      state.teams.list
  );
  const hasValidTeams = !(
    teamsList.length === 0 ||
    (teamsList.length === 1 && teamsList[0].name.toLowerCase() === 'default')
  );
  const userDateFormat = decodedUserToken()?.date_format;
  const shipDatePickerFormat =
    userDateFormat === 'DD/MM/YYYY'
      ? 'ddd, D MMM'
      : userDateFormat === 'YYYY/MM/DD'
        ? 'ddd, YYYY MMM D'
        : 'ddd, MMM D';
  const [step, setStep] = useState<Step>('form');
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [quoteWarnings, setQuoteWarnings] = useState<string[]>([]);
  const [rates, setRates] = useState<CarrierRate[]>([]);
  const [selectedRate, setSelectedRate] = useState<CarrierRate | null>(null);
  const [bookingResult, setBookingResult] =
    useState<CarrierShipResponse | null>(null);
  const [lastQuotePayload, setLastQuotePayload] =
    useState<CarrierQuotePayload | null>(null);
  const [savedFormValues, setSavedFormValues] = useState<FormValues | null>(
    null
  );

  // Account number selection (loaded for the selected carrier)
  const [carrierAccounts, setCarrierAccounts] = useState<
    CompanyCourierAccount[]
  >([]);
  const [selectedAccountNumber, setSelectedAccountNumber] = useState<
    string | undefined
  >(undefined);

  // Third-party billing: chosen via the "Bill to account" dropdown.
  const [thirdPartyAccountNumber, setThirdPartyAccountNumber] = useState('');
  const [thirdPartyPostalCode, setThirdPartyPostalCode] = useState('');
  const billThirdParty = selectedAccountNumber === THIRD_PARTY_ACCOUNT;

  // Email label (return / result step)
  const [emailTo, setEmailTo] = useState('');
  const [emailStatus, setEmailStatus] = useState<
    'idle' | 'sending' | 'sent' | 'error'
  >('idle');
  const [labelPngBase64, setLabelPngBase64] = useState<string | null>(null);
  /** Set when the carrier booked but saving the shipment failed; enables "Save shipment". */
  const [saveFailed, setSaveFailed] = useState(false);
  const [bookingFailure, setBookingFailure] = useState<BookingFailure | null>(
    null
  );
  const bookingKeyRef: BookingKeyRef = useRef(null);
  /** Counts Get quote clicks: a click whose check was overtaken by a later click leaves the outcome to that click. */
  const quoteRun = useRef(0);
  const bookingInFlightRef = useRef(false);
  const labelPngCacheRef = useRef(new Map<string, Promise<Blob | null>>());
  const printObjectUrlsRef = useRef<string[]>([]);
  const singleLabelPngRef = useRef<Blob | null>(null);
  /** Caps auto-suffix loops when Lookup keeps finding collisions. */
  const returnRefBumpCountRef = useRef(0);
  const [returnRefBumpExhausted, setReturnRefBumpExhausted] = useState(false);
  /** Taken RET-* refs discovered via Lookup while allocating a free return ref. */
  const takenReturnRefsRef = useRef<Set<string>>(new Set());

  // Reference field lookup
  const [referenceValue, setReferenceValue] = useState<string>(() => {
    if (isReturnMode) {
      return (
        outboundToReturnQuoteFormValues(returnSourceShipment).reference ?? ''
      );
    }
    return shellShipment?.reference_number ?? '';
  });
  const [refStatus, setRefStatus] = useState<RefStatus>('idle');
  const [refMatchedShipmentId, setRefMatchedShipmentId] = useState<
    string | null
  >(isReturnMode ? null : (shellShipment?.id ?? null));
  const lookupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const accountsRequestIdRef = useRef(0);
  const [selectedTeams, setSelectedTeams] = useState<TeamDropdownSelectTeam[]>(
    []
  );
  const [allTeams, setAllTeams] = useState<TeamDropdownSelectTeam[]>([]);
  const [teamsSearch, setTeamsSearch] = useState('');
  const [debouncedTeamsSearch, setDebouncedTeamsSearch] = useState('');
  /** Products and quantity per box, keyed by `PackageLineValues.key`; serials are form values. */
  const [lineProducts, setLineProducts] = useState<
    Record<string, SelectedProduct[]>
  >({});
  const linesRef = useRef<PackageLinesFieldsHandle>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [returnOutboundTotals, setReturnOutboundTotals] = useState<Record<
    string,
    number
  > | null>(null);
  const [productsPrefillStatus, setProductsPrefillStatus] =
    useState<ProductsPrefillStatus>(isReturnMode ? 'loading' : 'idle');

  const {
    products: paginatedProducts,
    getProductName,
    mergeIntoLookup,
    setSearch: setProductSearch,
    searchInputKey: productSearchInputKey,
    loading: productDropdownLoading,
    catalogTotalCount: productCatalogTotalCount,
    search: productSearch,
    handlePopupScroll: handleProductPopupScroll,
  } = usePaginatedProductDropdown({
    token: token ?? '',
  });

  // <Form> only renders on the first step; without preserve, watches go undefined after it unmounts.
  const watchOptions = { form, preserve: true };
  const pickupCountry = Form.useWatch(['pickup', 'country'], watchOptions);
  const deliveryCountry = Form.useWatch(['delivery', 'country'], watchOptions);
  const pickupStateOptions = getStateOptions(pickupCountry);
  const deliveryStateOptions = getStateOptions(deliveryCountry);
  const selectedProvider =
    (Form.useWatch('provider', watchOptions) as
      FormValues['provider'] | undefined) || 'FEDEX';
  const lithiumBatteriesOn = Boolean(
    Form.useWatch('lithiumBatteries', watchOptions)
  );
  /** UPS HazMat ship needs an emergency phone; FedEx lithium does not. */
  const pickupPhoneRequired = selectedProvider === 'UPS' && lithiumBatteriesOn;
  const providerLabel = selectedProvider === 'UPS' ? 'UPS' : 'FedEx';
  const providerCourierId =
    selectedProvider === 'UPS' ? UPS_COURIER_ID : FEDEX_COURIER_ID;

  const providerOptions = PROVIDER_OPTIONS;

  const promoteTargetId = quotePromoteTargetId(
    isReturnMode,
    shellShipment?.id,
    refMatchedShipmentId
  );
  const showTeamsAndProducts = shouldShowQuoteTeamsProducts(
    isReturnMode,
    refStatus,
    promoteTargetId
  );

  const lines = Form.useWatch(lineShapes, watchOptions);
  const boxLists = useMemo(
    () => expandLineProducts(lines, lineProducts),
    [lines, lineProducts]
  );
  const boxesBySerial = useMemo(() => serialBoxes(boxLists), [boxLists]);
  const manyBoxes = multiBoxEnabled && totalBoxes(lines) > 1;
  const productsOf = (key: string): SelectedProduct[] =>
    productsForKey(lineProducts, key);

  useEffect(() => {
    const urls = printObjectUrlsRef.current;
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedTeamsSearch(teamsSearch);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [teamsSearch]);

  useEffect(() => {
    if (!token || !hasValidTeams) return;
    let cancelled = false;
    const teamsRequest = debouncedTeamsSearch
      ? getTeamsDDLOFFeature(token, 'Shipment', debouncedTeamsSearch)
      : getTeamsDDLOFFeature(token, 'Shipment');
    const _teams = teamsRequest.then((val) => {
      if (cancelled) return;
      const teams = (val || []) as TeamDropdownSelectTeam[];
      setAllTeams(teams);
    });
    return () => {
      cancelled = true;
    };
  }, [debouncedTeamsSearch, token, hasValidTeams]);

  const handleTeamsSearch = useCallback((val: string) => {
    setTeamsSearch(val);
  }, []);

  const productsTouchedRef = useRef(false);

  const markProductsTouched = useCallback(() => {
    productsTouchedRef.current = true;
  }, []);

  const setProductsOfLine = (
    key: string,
    products: SelectedProduct[]
  ): void => {
    markProductsTouched();
    setLineProducts((prev) => ({ ...prev, [key]: products }));
  };

  // BR-14: the outbound-totals warning is for returns with more than one box.
  const totalsDiffs =
    returnOutboundTotals && manyBoxes
      ? diffAgainstOutbound(returnOutboundTotals, boxLists)
      : [];
  const returnTotalsNotice =
    totalsDiffs.length > 0 ? (
      <div role='alert' className={styles.productsWarning}>
        Assigned products differ from the outbound shipment. You can still save.
        {totalsDiffs.map((d) => (
          <div key={d.id}>
            {getProductName(d.id)}: {d.assigned} assigned, {d.outbound} on the
            outbound shipment
          </div>
        ))}
      </div>
    ) : undefined;

  const lineProductsSlot: LineProductsSlot | undefined = showTeamsAndProducts
    ? {
        render: (index, line) => (
          <LineProductsFields
            lineIndex={index}
            lineKey={line.key}
            qty={lineQty(line)}
            products={productsOf(line.key)}
            onChange={(products) => setProductsOfLine(line.key, products)}
            select={{
              token: token ?? '',
              form,
              getProductName,
              mergeIntoLookup,
              paginatedProducts,
              productCatalogTotalCount,
              productDropdownLoading,
              productSearchInputKey,
              productSearch,
              setProductSearch,
              handlePopupScroll: handleProductPopupScroll,
            }}
          />
        ),
        productsOf,
        getProductName,
        serialWarning: (lineIndex, box, productId) => {
          const piece = pieceOffset(lines, lineIndex) + box;
          const product = boxLists[piece]?.find((p) => p.id === productId);
          return product
            ? serialClashWarning(boxLists, piece, product, boxesBySerial)
            : undefined;
        },
        onLineAdded: (key, copiedFromKey) => {
          const copied = copiedFromKey ? productsOf(copiedFromKey) : [];
          if (copied.length > 0) setProductsOfLine(key, withoutSerials(copied));
        },
        onLineRemoved: (key) =>
          setLineProducts((prev) => withoutKey(prev, key)),
        onKeyReplaced: (from, to) =>
          setLineProducts((prev) => {
            const moved = prev[from];
            return Array.isArray(moved)
              ? { ...withoutKey(prev, from), [to]: moved }
              : prev;
          }),
        notice: returnTotalsNotice,
      }
    : undefined;

  const returnSourceId = returnSourceShipment?.id;
  const prefillShipmentIdRef = useRef<string | null>(null);

  useEffect(() => {
    productsTouchedRef.current = false;
  }, [returnSourceId]);

  useEffect(() => {
    if (!isReturnMode || !returnSourceId || !token) {
      if (isReturnMode) setProductsPrefillStatus('ready');
      return;
    }
    // Only prefill once per outbound shipment id so a later dep flicker
    // cannot overwrite user clears/edits or flip status back to loading.
    if (prefillShipmentIdRef.current === returnSourceId) return;

    let cancelled = false;
    setProductsPrefillStatus('loading');
    const preview = returnSourceShipment?.products_preview;
    const applyOutboundPrefill = (rows: SelectedProduct[]): void => {
      const { totals, selection } = outboundPrefill(
        rows,
        productsTouchedRef.current
      );
      setReturnOutboundTotals(totals);
      const firstKey: string | undefined = form.getFieldValue([
        'lines',
        0,
        'key',
      ]);
      if (selection && firstKey) {
        setLineProducts((prev) => ({
          ...prev,
          [firstKey]: withoutSerials(selection),
        }));
        const serials = Object.fromEntries(
          selection.flatMap(({ id, serial_number }) =>
            serial_number ? [[id, serial_number]] : []
          )
        );
        if (Object.keys(serials).length > 0) {
          form.setFieldValue(['lines', 0, 'boxes', 0, 'serials'], serials);
        }
      }
      setProductsPrefillStatus('ready');
    };
    fetchShipmentProducts(token, {
      shipment_id: returnSourceId,
      search: '',
      sort_key: '',
      sort_order: '',
      page: 1,
      page_size: 0,
    })
      .then((prodRes) => {
        if (cancelled) return;
        prefillShipmentIdRef.current = returnSourceId;
        const lines =
          (prodRes?.data?.data?.data?.products as ShipmentProductList[]) || [];
        let rows = selectedProductsFromOutboundLines(lines);
        if (rows.length === 0 && preview?.length) {
          rows = selectedProductsFromOutboundLines(preview);
        }
        mergeIntoLookup(
          rows
            .filter((r) => r.name)
            .map((r) => ({ id: r.id, name: r.name as string }))
        );
        applyOutboundPrefill(rows);
      })
      .catch(() => {
        if (cancelled) return;
        prefillShipmentIdRef.current = returnSourceId;
        if (preview?.length) {
          const rows = selectedProductsFromOutboundLines(preview);
          mergeIntoLookup(
            rows
              .filter((r) => r.name)
              .map((r) => ({ id: r.id, name: r.name as string }))
          );
          applyOutboundPrefill(rows);
          return;
        }
        setProductsPrefillStatus(
          productsTouchedRef.current ? 'ready' : 'failed'
        );
      });
    return () => {
      cancelled = true;
    };
  }, [
    isReturnMode,
    returnSourceId,
    returnSourceShipment?.products_preview,
    token,
    mergeIntoLookup,
    form,
  ]);

  // Keep form provider in sync when opening from a UPS shell before first paint settles.
  useEffect(() => {
    const desired = initialProviderFromShipment(
      isReturnMode ? returnSourceShipment : shellShipment
    );
    if (form.getFieldValue('provider') !== desired) {
      form.setFieldsValue({ provider: desired });
    }
  }, [form, shellShipment, returnSourceShipment, isReturnMode]);

  useEffect(() => {
    if (!token) return;
    const requestId = ++accountsRequestIdRef.current;
    setCarrierAccounts([]);
    // Always clear bill-to when switching carriers (including return mode) so
    // a FedEx account number is never sent to UPS or vice versa.
    setSelectedAccountNumber(undefined);
    setThirdPartyAccountNumber('');
    setThirdPartyPostalCode('');
    const loadAccounts =
      selectedProvider === 'UPS' ? GetUPSAccounts : GetFedExAccounts;
    loadAccounts(token)
      .then((res) => {
        if (requestId !== accountsRequestIdRef.current) return;
        const envelope = (
          res?.data as { data?: Record<string, unknown> } | undefined
        )?.data;
        const accts = (envelope?.accounts ??
          (envelope?.data as Record<string, unknown> | undefined)?.accounts) as
          CompanyCourierAccount[] | undefined;
        if (Array.isArray(accts) && accts.length > 0) {
          setCarrierAccounts(accts);
          // Return mode: leave billing blank so the user must choose.
          if (!isReturnMode) {
            const def = accts.find((a) => a.is_default) ?? accts[0];
            setSelectedAccountNumber(def.account_number);
          }
        }
      })
      .catch(() => {
        /* non-fatal — fall back to backend default */
      });
  }, [token, isReturnMode, selectedProvider]);

  // ---------------------------------------------------------------------------
  // Web Serial — Zebra USB printer
  // ---------------------------------------------------------------------------
  // Zebra's USB vendor ID (all models: ZD410, ZD420, ZD620, GK420, …)
  const ZEBRA_USB_VENDOR_ID = 0x0a5f;

  /** Whether the current browser supports the Web Serial API (Chrome / Edge). */
  const serialSupported =
    typeof navigator !== 'undefined' && 'serial' in navigator;

  /** A previously-granted (or freshly-connected) Zebra serial port. */
  const [serialPort, setSerialPort] = useState<SerialPort | null>(null);
  const [zebraPrintStatus, setZebraPrintStatus] = useState<
    'idle' | 'printing' | 'sent' | 'error'
  >('idle');

  // Labelary-rendered PNG preview of the ZPL label (async, best-effort)
  const [labelPreviewUrl, setLabelPreviewUrl] = useState<string | null>(null);
  const [labelPreviewLoading, setLabelPreviewLoading] =
    useState<boolean>(false);

  // On mount: silently pick up a Zebra port that was already granted in a
  // previous session so the user doesn't have to re-pair every time.
  useEffect(() => {
    if (!serialSupported) return;
    const _ports = navigator.serial.getPorts().then((ports) => {
      const zebra = ports.find(
        (p) => p.getInfo().usbVendorId === ZEBRA_USB_VENDOR_ID
      );
      if (zebra) setSerialPort(zebra);
    });
  }, [serialSupported]);

  /** Show the browser's USB port picker, filtered to Zebra devices only. */
  const handleConnectZebra = async (): Promise<void> => {
    if (!serialSupported) return;
    try {
      const port = await navigator.serial.requestPort({
        filters: [{ usbVendorId: ZEBRA_USB_VENDOR_ID }],
      });
      setSerialPort(port);
    } catch {
      /* User cancelled the picker — no-op */
    }
  };

  /** Send raw ZPL bytes to the paired Zebra port.
   *  Opens → writes → closes so each job is independent; jobs run one at a time
   *  because a serial port cannot be opened twice. */
  const zebraQueueRef = useRef(createSerialQueue());
  const sendZplViaSerial = (zpl: string): Promise<void> =>
    zebraQueueRef.current(async () => {
      if (!serialPort) throw new Error('No Zebra printer connected.');
      await serialPort.open({ baudRate: 9600 });
      const writer = serialPort.writable.getWriter();
      try {
        await writer.write(new TextEncoder().encode(zpl));
      } finally {
        writer.releaseLock();
        await serialPort.close();
      }
    });

  const downloadLabelFile = (label: BookedLabel): void => {
    const zpl = isZplContentType(label.labelContentType);
    const ext = zpl
      ? 'zpl'
      : label.labelContentType.includes('png')
        ? 'png'
        : 'pdf';
    const objectUrl = zpl
      ? URL.createObjectURL(
          new Blob([atob(label.labelBase64)], { type: 'text/plain' })
        )
      : null;
    const a = document.createElement('a');
    a.href =
      objectUrl ?? `data:${label.labelContentType};base64,${label.labelBase64}`;
    a.download = `${selectedProvider === 'UPS' ? 'ups' : 'fedex'}-label-${label.trackingNumber}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  };

  const handleDownloadZpl = (): void => {
    if (!bookingResult?.labelBase64) return;
    downloadLabelFile({
      sequence: 1,
      trackingNumber: bookingResult.trackingNumber ?? 'label',
      labelBase64: bookingResult.labelBase64,
      labelContentType: 'application/x-zpl',
    });
  };

  const printZplToZebra = async (zpl: string): Promise<void> => {
    setZebraPrintStatus('printing');
    try {
      await sendZplViaSerial(zpl);
      setZebraPrintStatus('sent');
    } catch {
      setZebraPrintStatus('error');
    }
  };

  const handlePrintToZebra = async (): Promise<void> => {
    if (!bookingResult?.labelBase64 || !serialPort) return;
    await printZplToZebra(atob(bookingResult.labelBase64));
  };

  /** Render a ZPL label to PNG via Labelary's free public API (best-effort).
   *  Returns null if Labelary is unavailable. */
  const fetchLabelaryPreview = async (
    zplBase64: string
  ): Promise<Blob | null> => {
    setLabelPreviewLoading(true);
    setLabelPreviewUrl(null);
    try {
      const png = await renderZplToPng(zplBase64);
      if (png) setLabelPreviewUrl(URL.createObjectURL(png));
      return png;
    } finally {
      setLabelPreviewLoading(false);
    }
  };

  /** One throttled Labelary render per box, shared by preview, save, print and email. */
  const getLabelPng = useCallback(
    (label: BookedLabel): Promise<Blob | null> => {
      return renderOnce(labelPngCacheRef.current, label.trackingNumber, () =>
        isZplContentType(label.labelContentType)
          ? renderZplToPng(label.labelBase64)
          : Promise.resolve(null)
      );
    },
    []
  );

  useEffect(() => {
    const ref = referenceValue.trim();
    if (lookupTimerRef.current) clearTimeout(lookupTimerRef.current);
    if (!ref || !token) {
      setRefStatus('idle');
      setRefMatchedShipmentId(null);
      return;
    }
    setRefStatus('checking');
    lookupTimerRef.current = setTimeout(async () => {
      try {
        const res = await LookupShipmentByReference(token, ref);
        const data = (
          res?.data as {
            data?: { data?: { match?: string; shipment_id?: string | null } };
          }
        )?.data?.data;
        const match = (data?.match as RefStatus) ?? 'none';
        setRefStatus(match);
        setRefMatchedShipmentId(data?.shipment_id ?? null);

        // Return mode: if RET-* already exists, auto-bump to RET-…-2, -3, …
        // before the user quotes — avoids FedEx book succeeding then create failing.
        if (
          isReturnMode &&
          (match === 'booked' || match === 'shell') &&
          returnRefBumpCountRef.current < 20
        ) {
          returnRefBumpCountRef.current += 1;
          takenReturnRefsRef.current.add(ref);
          const bumped = nextReturnReference(
            returnSourceShipment?.reference_number,
            takenReturnRefsRef.current
          );
          if (bumped && bumped !== ref) {
            form.setFieldsValue({ reference: bumped });
            setReferenceValue(bumped);
          } else if (returnRefBumpCountRef.current >= 20) {
            setReturnRefBumpExhausted(true);
          }
        } else if (isReturnMode && (match === 'booked' || match === 'shell')) {
          setReturnRefBumpExhausted(true);
        } else if (match === 'none') {
          returnRefBumpCountRef.current = 0;
          setReturnRefBumpExhausted(false);
        }
      } catch {
        setRefStatus('none');
        setRefMatchedShipmentId(null);
      }
    }, 500);
    return () => {
      if (lookupTimerRef.current) clearTimeout(lookupTimerRef.current);
    };
  }, [
    referenceValue,
    token,
    isReturnMode,
    returnSourceShipment?.reference_number,
    form,
  ]);

  const initialValues = useMemo<FormValues>(() => {
    const defaultProvider = initialProviderFromShipment(
      isReturnMode ? returnSourceShipment : shellShipment
    );
    if (isReturnMode) {
      const fromReturn = outboundToReturnQuoteFormValues(returnSourceShipment);
      return {
        provider: defaultProvider,
        pickup: { country: 'US', ...fromReturn.pickup },
        delivery: { country: 'US', ...fromReturn.delivery },
        shipDate: fromReturn.shipDate ?? dayjs(),
        reference: fromReturn.reference ?? '',
        lines: [
          {
            ...emptyLine(),
            weight: fromReturn.weight,
            length: fromReturn.length,
            width: fromReturn.width,
            height: fromReturn.height,
          },
        ],
      };
    }
    const fromShell = shellToQuoteFormValues(shellShipment);
    return {
      provider: defaultProvider,
      pickup: { country: 'US', ...fromShell.pickup },
      delivery: { country: 'US', ...fromShell.delivery },
      shipDate: fromShell.shipDate ?? dayjs(),
      reference: fromShell.reference ?? '',
      lines: [emptyLine()],
    };
  }, [shellShipment, returnSourceShipment, isReturnMode]);

  const attachLabel = async (
    data: CarrierShipResponse,
    shipmentId: string,
    tok: string,
    /** PNG blob from Labelary — preferred over raw ZPL since the backend
     *  file-upload endpoint only accepts PDF/PNG. When present this is
     *  uploaded instead of the raw label data. */
    pngBlob?: Blob | null
  ): Promise<void> => {
    const labelPrefix =
      selectedProvider === 'UPS' ? 'ups-label' : 'fedex-label';
    try {
      if (pngBlob) {
        // Upload the Labelary-rendered PNG (backend accepts image/png)
        const file = new File(
          [pngBlob],
          `${labelPrefix}-${data.trackingNumber ?? 'label'}.png`,
          { type: 'image/png' }
        );
        const fd = new FormData();
        fd.append('file', file);
        fd.append('shipment_id', shipmentId);
        await uploadFile(tok, fd);
        return;
      }

      // Fallback: upload raw data only for non-ZPL labels (PDF/PNG).
      // ZPL/EPL are skipped — the backend rejects them and would fire a
      // global error toast via the Axios interceptor.
      if (!data.labelBase64) return;
      const mime = (data.labelContentType || 'application/pdf').toLowerCase();
      if (mime.includes('zpl') || mime.includes('epl')) return;

      const ext = mime.includes('png') ? 'png' : 'pdf';
      const binary = atob(data.labelBase64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      const file = new File(
        [bytes],
        `${labelPrefix}-${data.trackingNumber}.${ext}`,
        { type: mime }
      );
      const fd = new FormData();
      fd.append('file', file);
      fd.append('shipment_id', shipmentId);
      await uploadFile(tok, fd);
    } catch {
      // Best-effort — the user already sees the label in this drawer.
    }
  };

  const bookedBoxCount = lastQuotePayload?.pieces?.length ?? 1;
  const multiLabels = useMemo(
    () =>
      bookedBoxCount > 1 && bookingResult ? bookedLabels(bookingResult) : [],
    [bookedBoxCount, bookingResult]
  );

  /** Multi-box shipments attach one PNG per box, named by its tracking number. */
  const attachLabels = async (
    data: CarrierShipResponse,
    shipmentId: string,
    tok: string,
    pngBlob?: Blob | null
  ): Promise<void> => {
    if (bookedBoxCount < 2) {
      await attachLabel(data, shipmentId, tok, pngBlob);
      return;
    }
    for (const label of bookedLabels(data)) {
      await attachLabel(
        {
          ...data,
          trackingNumber: label.trackingNumber,
          labelBase64: label.labelBase64,
          labelContentType: label.labelContentType,
        },
        shipmentId,
        tok,
        await getLabelPng(label)
      );
    }
  };

  /** Per-box create/promote `pieces[]` (tracking numbers joined to size and products). */
  const bookedPieces = (
    data: CarrierShipResponse,
    boxOneProducts: SelectedProduct[]
  ) => {
    const quotePieces = lastQuotePayload?.pieces;
    // Products fetched at booking time (the return prefill is still loading or failed) were never shown, so they go into box 1 only, serials included, instead of being repeated per box.
    const lists = boxLists[0]?.length
      ? boxLists
      : [boxOneProducts, ...boxLists.slice(1)];
    const bookedBoxLists = quotePieces
      ? lists.slice(0, quotePieces.length)
      : [];
    const boxPieces =
      quotePieces && data.pieces?.length
        ? buildCreatePieces(quotePieces, data.pieces, bookedBoxLists)
        : undefined;
    return { bookedBoxLists, boxPieces };
  };

  const bookingIdentity = (data: CarrierShipResponse) => ({
    courierId: providerCourierId,
    trackingNumber: data.trackingNumber ?? '',
  });

  const fetchSavedShipment = (id: string) =>
    GetSingleShipment(token ?? '', id, true, false, 'none');

  const findShipmentIdByReference = async (
    reference: string
  ): Promise<string | null> => {
    try {
      const res = await LookupShipmentByReference(token ?? '', reference);
      const found = (
        res?.data as {
          data?: { data?: { match?: string; shipment_id?: string | null } };
        }
      )?.data?.data;
      return found?.match === 'booked' ? (found.shipment_id ?? null) : null;
    } catch {
      return null;
    }
  };

  /** Saves the booked shipment (create or promote) and attaches the labels. */
  const persistBooking = async (
    data: CarrierShipResponse,
    pngBlob: Blob | null
  ): Promise<void> => {
    if (!lastQuotePayload || !token) return;
    // Return mode never promotes the outbound — always create a new shipment.
    const bookedPromoteTargetId = quotePromoteTargetId(
      isReturnMode,
      shellShipment?.id,
      refMatchedShipmentId
    );

    // The account actually billed for this shipment — the third-party account
    // when one was entered, otherwise the selected/default company account.
    const billedAccountNumber =
      lastQuotePayload.thirdPartyAccountNumber ||
      lastQuotePayload.accountNumber ||
      undefined;

    if (bookedPromoteTargetId && data.trackingNumber) {
      try {
        const promotePieces = bookedPieces(data, boxLists[0] ?? []).boxPieces;
        // A retry may follow a promote whose response was lost: do not repeat the write then.
        const alreadyPromoted = saveFailed
          ? await findSavedBooking(
              bookedPromoteTargetId,
              bookingIdentity(data),
              fetchSavedShipment
            )
          : null;
        if (!alreadyPromoted) {
          await PromoteShipment(token, bookedPromoteTargetId, {
            courier_id: providerCourierId,
            courier_tracking_id: data.trackingNumber,
            courier_service_type: data.serviceType || undefined,
            courier_account_number: billedAccountNumber,
            ...(promotePieces ? { pieces: promotePieces } : {}),
          });
        }
        await attachLabels(data, bookedPromoteTargetId, token, pngBlob);
        setSaveFailed(false);
        onPromoteSuccess?.();
      } catch {
        setSaveFailed(true);
        setError(
          'Label generated, but linking the shipment failed. Open the shipment to enter the tracking number manually.'
        );
      }
    } else if (!bookedPromoteTargetId && data.trackingNumber) {
      const fv = savedFormValues;
      /** Build a freeform address string for LocationV2.address. */
      const addrParts = (party?: PartyValues): string =>
        [
          party?.street,
          party?.street2,
          party?.city,
          normalizeStateCode(party?.state, party?.country) || party?.state,
          normalizePostalCode(party?.postalCode, party?.country) ||
            party?.postalCode,
          normalizeCountryCode(party?.country) || party?.country,
        ]
          .filter(Boolean)
          .join(', ');
      /**
       * Prefer existing LocationV2 UUIDs (return mode: flipped outbound
       * locations). Only fall back to name/address create when IDs are
       * missing. Backend geocodes FedEx/UPS freeform O/D when lat/lng are
       * absent (server-side carrier gate — not this client flag).
       * `geocode_if_missing` is retained for clarity / older servers.
       */
      const locationPayload = (
        existingId: string | undefined,
        party: PartyValues | undefined,
        fallbackName: string
      ):
        | {
            id: string;
            geocode_if_missing: true;
          }
        | {
            name: string;
            address: string;
            geocode_if_missing: true;
          }
        | undefined => {
        // Reuse existing LocationV2 but still opt into geocode so rows that
        // were created without lat/lng (prior quote bug) get pins filled.
        if (existingId) {
          return { id: existingId, geocode_if_missing: true };
        }
        const address = addrParts(party);
        if (!address) return undefined;
        const name =
          party?.company?.trim() ||
          party?.name?.trim() ||
          party?.street?.trim() ||
          fallbackName;
        return { name, address, geocode_if_missing: true };
      };
      try {
        const dims = dimsPayload(singleBox(fv ?? {}));
        const weightLb = totalWeightLb(boxesFromLines(fv?.lines));
        // Return: pickup = outbound dest, delivery = outbound origin.
        const fromLocation = locationPayload(
          isReturnMode
            ? returnSourceShipment?.to_location?.id
            : shellShipment?.from_location?.id,
          fv?.pickup,
          'Origin'
        );
        const toLocation = locationPayload(
          isReturnMode
            ? returnSourceShipment?.from_location?.id
            : shellShipment?.to_location?.id,
          fv?.delivery,
          'Destination'
        );
        const outboundHadLinkCode = Boolean(
          returnSourceShipment?.link_code?.trim()
        );
        const sharedReturnLinkCode = isReturnMode
          ? returnSourceShipment?.link_code?.trim() ||
            returnSourceShipment?.reference_number?.trim() ||
            undefined
          : undefined;

        let boxOneProducts = boxLists[0] ?? [];
        let createProducts = productsPayloadFromSelectedRows(boxOneProducts);
        if (
          shouldFallbackReturnProductsFetch(
            isReturnMode,
            productsPrefillStatus,
            createProducts.length,
            productsTouchedRef.current
          ) &&
          returnSourceShipment?.id &&
          token
        ) {
          try {
            const prodRes = await fetchShipmentProducts(token, {
              shipment_id: returnSourceShipment.id,
              search: '',
              sort_key: '',
              sort_order: '',
              page: 1,
              page_size: 0,
            });
            const lines =
              (prodRes?.data?.data?.data?.products as ShipmentProductList[]) ||
              [];
            boxOneProducts = selectedProductsFromOutboundLines(lines);
            createProducts = productsPayloadFromSelectedRows(boxOneProducts);
          } catch (err) {
            console.warn(
              'Failed to fetch outbound products for return; trying preview',
              err
            );
          }
          if (
            createProducts.length === 0 &&
            returnSourceShipment.products_preview?.length
          ) {
            boxOneProducts = selectedProductsFromOutboundLines(
              returnSourceShipment.products_preview
            );
            createProducts = productsPayloadFromSelectedRows(boxOneProducts);
          }
        }

        const { bookedBoxLists, boxPieces } = bookedPieces(
          data,
          boxOneProducts
        );
        const flatProducts = lastQuotePayload.pieces
          ? summedProductsPayload(bookedBoxLists)
          : createProducts;

        const createShipmentBody = {
          reference_number: fv?.reference || referenceValue.trim(),
          courier: providerCourierId,
          courier_tracking_id: data.trackingNumber,
          courier_service_type: data.serviceType || '',
          courier_account_number: billedAccountNumber,
          ...(fromLocation ? { from_location: fromLocation } : {}),
          ...(toLocation ? { to_location: toLocation } : {}),
          customer_preferred_pickup_date: fv?.shipDate
            ? fv.shipDate.toISOString()
            : undefined,
          ...(weightLbToKg(weightLb) !== undefined
            ? { weight_kg: weightLbToKg(weightLb) }
            : {}),
          ...(dims.dimensionUnit
            ? { volume: `${dims.length}L x ${dims.width}W x ${dims.height}H` }
            : {}),
          // Share link_code with outbound so Control Tower link-code
          // filter groups the return with the original. Mirror auto-return
          // ingest: reuse outbound link_code, else outbound reference.
          ...(isReturnMode && sharedReturnLinkCode
            ? { link_code: sharedReturnLinkCode }
            : {}),
          ...(boxPieces
            ? { pieces: boxPieces }
            : flatProducts.length > 0
              ? { products: flatProducts }
              : {}),
          teams: selectedTeams.map((t) => t.id),
        };
        // A retry may follow a create whose response was lost: reuse that shipment.
        let createdId = saveFailed
          ? await findSavedBooking(
              await findShipmentIdByReference(
                createShipmentBody.reference_number
              ),
              bookingIdentity(data),
              fetchSavedShipment
            )
          : null;
        if (!createdId) {
          const createRes = await CreateNewShipment(token, createShipmentBody);
          // Match BookShipmentDrawer nesting, with a one-level fallback.
          const body = createRes?.data as {
            data?: { data?: { id?: string }; id?: string };
            id?: string;
          };
          createdId =
            body?.data?.data?.id || body?.data?.id || body?.id || null;
        }
        if (createdId) {
          await attachLabels(data, createdId, token, pngBlob);
        }
        // Backfill outbound when it had no link_code so both share one value.
        if (
          isReturnMode &&
          sharedReturnLinkCode &&
          !outboundHadLinkCode &&
          returnSourceShipment?.id
        ) {
          try {
            await UpdateShipment(token, {
              shipment_id: returnSourceShipment.id,
              link_code: sharedReturnLinkCode,
            });
          } catch (err) {
            // Non-fatal: return already has link_code; outbound can be
            // edited manually if this patch fails.
            console.warn('Failed to backfill outbound link_code', err);
          }
        }
        setSaveFailed(false);
        onPromoteSuccess?.();
      } catch {
        setSaveFailed(true);
        setError(
          isReturnMode
            ? 'Label generated, but creating the return shipment failed (reference may already exist). Note the tracking number above and try a different RET- reference.'
            : 'Label generated, but creating the shipment record failed. Note the tracking number above.'
        );
      }
    }
  };

  const handleGetQuote = async (): Promise<void> => {
    const run = ++quoteRun.current;
    setError(null);
    setBookingFailure(null);
    setQuoteWarnings([]);
    // Return mode always creates a new shipment — auto-suffix handles most
    // collisions, but still block quote if Lookup still reports taken.
    if (isReturnMode && (refStatus === 'booked' || refStatus === 'shell')) {
      setError(
        'This return reference is already in use. Wait for auto-suffix or edit the reference.'
      );
      return;
    }
    if (!isReturnMode && refStatus === 'booked') {
      setError(
        `A shipment with this ${themeT(
          'reference',
          theme.themeType
        )?.toLowerCase()}${suffix} already has tracking info. Change the ${themeT(
          'reference',
          theme.themeType
        )?.toLowerCase()}${suffix} to continue.`
      );
      return;
    }
    let values: FormValues;
    try {
      // validateFields returns registered fields only; a line's `qty` and `key` have no Form.Item.
      values = {
        ...(await validateAllFields(form, () => run === quoteRun.current)),
        lines: form.getFieldValue('lines'),
      };
    } catch (err) {
      if (run !== quoteRun.current) return;
      const rejection = err as { errorFields?: unknown[]; outOfDate?: boolean };
      if (rejection?.outOfDate && !rejection.errorFields?.length) {
        setError(
          'The form changed while it was being checked. Please try again.'
        );
        return;
      }
      setSubmitAttempted(true);
      const name = firstErrorFieldName(form, err);
      if (name?.[0] === 'lines' && typeof name[1] === 'number') {
        linesRef.current?.reveal(name[1]); // the editor scrolls to the field once the line shows
      } else if (name) {
        form.scrollToField(name, SCROLL_TO_FIELD);
      }
      return;
    }
    if (run !== quoteRun.current) return;
    setSubmitAttempted(false);
    // Force FedEx-safe codes into the visible form (California → CA, ZIP+4 → 5).
    values = syncNormalizedPartyFields(form, values);
    setSavedFormValues(values);
    if (!token) {
      setError(`You must be signed in to request a ${providerLabel} quote.`);
      return;
    }

    if (showTeamsAndProducts && hasCrossBoxDuplicateSerial(boxLists)) {
      setError(
        'A serial number is used in more than one box. Each serial can be in one box only.'
      );
      return;
    }

    if (isReturnMode && !selectedAccountNumber) {
      setError('Select a bill-to account to continue.');
      return;
    }

    if (billThirdParty) {
      if (!thirdPartyAccountNumber.trim() || !thirdPartyPostalCode.trim()) {
        setError(
          'Enter both the third-party account number and postal code to continue.'
        );
        return;
      }
    }

    if (
      values.lithiumBatteries &&
      (values.provider || selectedProvider) === 'UPS'
    ) {
      const pickupPhone = phoneDigitsWithoutExtension(values.pickup?.phone);
      if (pickupPhone.length < 10) {
        setError(
          'Pickup phone is required for UPS lithium battery shipments (emergency contact).'
        );
        return;
      }
    }

    const payload: CarrierQuotePayload = {
      ...buildPayload(values),
      ...(isReturnMode
        ? {
            returnLabel: true,
            rmaReference:
              returnSourceShipment?.reference_number?.trim() || undefined,
          }
        : {}),
      ...(billThirdParty
        ? {
            thirdPartyAccountNumber: thirdPartyAccountNumber.trim(),
            thirdPartyPostalCode:
              normalizePostalCode(thirdPartyPostalCode, 'US') ||
              thirdPartyPostalCode.trim(),
          }
        : selectedAccountNumber
          ? { accountNumber: selectedAccountNumber }
          : {}),
    };
    setLastQuotePayload(payload);
    setLoading(`Requesting rates from ${providerLabel}…`);
    try {
      let fetchedRates: CarrierRate[] = [];
      if (selectedProvider === 'UPS') {
        const res = await RequestUPSQuote(token, payload as UPSQuotePayload);
        const data = parseUPSQuoteResponse(res);
        fetchedRates = (data?.rates ?? []) as CarrierRate[];
        setQuoteWarnings(
          (data?.warnings ?? [])
            .map((w) => w.message?.trim())
            .filter((msg): msg is string => Boolean(msg))
        );
      } else {
        const res = await RequestFedExQuote(token, payload);
        const data = parseFedExQuoteResponse(res);
        fetchedRates = data?.rates ?? [];
      }
      if (!fetchedRates.length) {
        setError(
          `No ${providerLabel} services were returned for this route. Try different addresses or weight.`
        );
        return;
      }
      const sorted = [...fetchedRates].sort(
        (a, b) => (a.totalNetCharge ?? 0) - (b.totalNetCharge ?? 0)
      );
      setRates(sorted);
      setSelectedRate(null);
      setStep('rates');
    } catch (err) {
      setError(formatQuoteApiError(err));
    } finally {
      setLoading(null);
    }
  };

  const handleBook = async (confirmDuplicate: boolean): Promise<void> => {
    if (
      !selectedRate ||
      !lastQuotePayload ||
      !token ||
      bookingInFlightRef.current
    ) {
      return;
    }
    bookingInFlightRef.current = true;
    setError(null);
    setBookingFailure(null);
    setLoading(`Booking shipment with ${providerLabel}…`);
    try {
      const shipPayload: CarrierShipPayload = {
        ...lastQuotePayload,
        serviceType: selectedRate.serviceType,
        ...(isReturnMode
          ? {
              returnLabel: true,
              rmaReference:
                returnSourceShipment?.reference_number?.trim() || undefined,
            }
          : {}),
        ...(confirmDuplicate ? { confirmDuplicate: true } : {}),
      };
      const attempt = await attemptBooking(
        bookingKeyRef,
        shipPayload,
        async (idempotencyKey) => {
          if (selectedProvider === 'UPS') {
            const res = await BookUPSShipment(
              token,
              shipPayload as UPSShipPayload,
              idempotencyKey
            );
            return parseUPSShipResponse(res);
          }
          const res = await BookFedExShipment(
            token,
            shipPayload,
            idempotencyKey
          );
          return parseFedExShipResponse(res);
        }
      );
      if (attempt.outcome === 'failed') {
        setBookingFailure(attempt.failure);
        setError(
          bookingFailureMessage(attempt.failure, providerLabel) ??
            formatQuoteApiError(attempt.error)
        );
        return;
      }
      const { data } = attempt;
      if (!data) {
        setError(
          `${providerLabel} booking succeeded but the response was empty.`
        );
        return;
      }
      if (!data.labelBase64 && !data.labelUrl && !data.trackingNumber) {
        setError(
          `${providerLabel} booking failed: no tracking number or label returned.`
        );
        return;
      }
      setBookingResult(data);
      setStep('result');

      const boxLabels = lastQuotePayload.pieces ? bookedLabels(data) : [];
      let pngBlob: Blob | null = null;
      if (boxLabels.length) {
        // Every box prints in one job; previews and PNGs load through the throttled queue.
        if (
          serialPort &&
          boxLabels.every((l) => isZplContentType(l.labelContentType))
        ) {
          const _ = printZplToZebra(concatZpl(boxLabels));
        }
      } else if (data.labelBase64) {
        // Fetch Labelary PNG preview and auto-send to Zebra in parallel.
        // We await the PNG so we can attach it to the shipment record below
        // (the backend rejects ZPL, but accepts PNG from Labelary).
        const pngBlobPromise = fetchLabelaryPreview(data.labelBase64);

        // Kick off Zebra print without waiting for Labelary
        if (serialPort) {
          setZebraPrintStatus('printing');
          sendZplViaSerial(atob(data.labelBase64))
            .then(() => setZebraPrintStatus('sent'))
            .catch(() => setZebraPrintStatus('error'));
        }

        pngBlob = await pngBlobPromise;
        if (pngBlob) {
          try {
            setLabelPngBase64(await blobToBase64(pngBlob));
          } catch {
            setLabelPngBase64(null);
          }
        }
      }
      singleLabelPngRef.current = pngBlob;
      await persistBooking(data, pngBlob);
    } catch (err) {
      setError(formatQuoteApiError(err));
    } finally {
      bookingInFlightRef.current = false;
      setLoading(null);
    }
  };

  const handleSaveShipment = async (): Promise<void> => {
    if (!bookingResult) return;
    setError(null);
    setLoading('Saving shipment…');
    try {
      await persistBooking(bookingResult, singleLabelPngRef.current);
    } finally {
      setLoading(null);
    }
  };

  const handleEmailLabel = async (): Promise<void> => {
    if (!token) return;
    const pngOrZpl = labelPngBase64 || bookingResult?.labelBase64 || null;
    if (!pngOrZpl && !multiLabels.length) {
      setError('No label is available to email yet.');
      return;
    }
    const dest = emailTo.trim();
    if (!dest || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dest)) {
      setError('Enter a valid email address.');
      return;
    }
    setError(null);
    setEmailStatus('sending');
    try {
      const content = multiLabels.length
        ? { labels: await emailAttachments(multiLabels) }
        : {
            label_base64: pngOrZpl ?? undefined,
            content_type: labelPngBase64 ? 'image/png' : 'application/x-zpl',
          };
      const payload = {
        to_email: dest,
        ...content,
        tracking_number: bookingResult?.trackingNumber || undefined,
        reference:
          savedFormValues?.reference || referenceValue.trim() || undefined,
      };
      await (selectedProvider === 'UPS' ? EmailUPSLabel : EmailFedExLabel)(
        token,
        payload
      );
      setEmailStatus('sent');
    } catch (err) {
      setEmailStatus('error');
      setError(formatQuoteApiError(err));
    }
  };

  /** One attachment per box: the Labelary PNG when available, else the carrier label. */
  const emailAttachments = (labels: BookedLabel[]): Promise<EmailLabelItem[]> =>
    Promise.all(
      labels.map(async (label) => {
        const png = await getLabelPng(label);
        return {
          trackingNumber: label.trackingNumber,
          labelBase64: png ? await blobToBase64(png) : label.labelBase64,
          labelContentType: png ? 'image/png' : label.labelContentType,
        };
      })
    );

  /** Prints labels in the order given: one ZPL stream to the Zebra, else one page of images. */
  const printLabelSet = async (
    labels: BookedLabel[],
    viaBrowser = false
  ): Promise<void> => {
    if (!labels.length) return;
    if (
      !viaBrowser &&
      serialPort &&
      labels.every((l) => isZplContentType(l.labelContentType))
    ) {
      await printZplToZebra(concatZpl(labels));
      return;
    }
    const w = window.open('', '_blank');
    if (!w) {
      setError('Popup blocked — allow popups to print the label.');
      return;
    }
    w.document.write('<p>Preparing labels…</p>');
    const srcs = await Promise.all(
      labels.map(async (label) => {
        if (isZplContentType(label.labelContentType)) {
          const png = await getLabelPng(label);
          if (!png) return null;
          const objectUrl = URL.createObjectURL(png);
          printObjectUrlsRef.current.push(objectUrl);
          return objectUrl;
        }
        return isImageContentType(label.labelContentType)
          ? `data:${label.labelContentType};base64,${label.labelBase64}`
          : null;
      })
    );
    if (srcs.some((src) => src === null)) {
      w.close();
      setError('Label images are unavailable. Download the labels instead.');
      return;
    }
    w.document.open();
    w.document.write(buildPrintHtml(srcs as string[]));
    w.document.close();
  };

  const handlePrint = (): void => {
    if (!bookingResult) return;
    const isZpl = bookingResult.labelContentType?.includes('zpl');
    if (isZpl) {
      if (serialPort) {
        const _ = handlePrintToZebra();
      } else {
        handleDownloadZpl();
      }
      return;
    }
    if (bookingResult.labelBase64) {
      const mime = bookingResult.labelContentType || 'application/pdf';
      const src = `data:${mime};base64,${bookingResult.labelBase64}`;
      const w = window.open(src, '_blank');
      if (!w) {
        setError('Popup blocked — allow popups to print the label.');
      }
      return;
    }
    if (bookingResult.labelUrl) {
      const w = window.open(bookingResult.labelUrl, '_blank');
      if (!w) {
        setError('Popup blocked — allow popups to print the label.');
      }
    }
  };

  /** Print the label as an image using the Labelary PNG preview (works for ZPL
   *  labels where the browser cannot render the raw ZPL data URI). Falls back
   *  to handlePrint() when no Labelary preview is available. */
  const handlePrintPdf = (): void => {
    if (labelPreviewUrl) {
      const w = window.open('', '_blank');
      if (!w) {
        setError('Popup blocked — allow popups to print the label.');
        return;
      }
      w.document.write(
        `<!DOCTYPE html><html><head><title>Print Label</title><style>
          body{margin:0;display:flex;justify-content:center;align-items:flex-start;}
          img{max-width:100%;height:auto;display:block;}
        </style></head><body>
          <img src="${labelPreviewUrl}" onload="window.print()">
        </body></html>`
      );
      w.document.close();
      return;
    }
    // Fallback: use raw data URI / URL (works for PDF labels)
    handlePrint();
  };

  const renderForm = (): React.ReactNode => (
    <Form
      className={styles.quoteForm}
      form={form}
      layout='vertical'
      initialValues={initialValues}
      requiredMark
      colon={false}
    >
      <CustomTitle
        text={
          isReturnMode ? 'Generate return label' : 'Quote & Book a shipment'
        }
        fontSize='24px'
        fontWeight='700'
        lineHeight='32px'
        marginBottom={24}
      />

      <section className={styles.section}>
        <Form.Item
          label={themeT('transportProvider', theme.themeType)}
          name='provider'
          rules={[
            {
              required: true,
              message: `Pick a ${themeT('transportProvider', theme.themeType)?.toLowerCase()}`,
            },
          ]}
        >
          <CustomSelect
            placeholder='Select'
            width='100%'
            height='36px'
            options={providerOptions}
          />
        </Form.Item>

        <Form.Item label='Bill to account'>
          <CustomSelect
            placeholder={
              isReturnMode ? 'Select account (required)' : 'Select account'
            }
            width='100%'
            height='36px'
            value={selectedAccountNumber}
            onChange={(val: string) => setSelectedAccountNumber(val)}
            options={[
              ...carrierAccounts.map((a) => ({
                value: a.account_number,
                label: a.description
                  ? `${a.account_number} — ${a.description}`
                  : a.account_number,
              })),
              { value: THIRD_PARTY_ACCOUNT, label: 'Third party account' },
            ]}
          />
        </Form.Item>

        {billThirdParty && (
          <>
            <div className={styles.thirdPartyRow}>
              <Form.Item
                label='Third-party account number'
                style={{ flex: 65 }}
              >
                <CustomInput
                  placeholder='Enter account number'
                  height='36px'
                  value={thirdPartyAccountNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setThirdPartyAccountNumber(e.target.value)
                  }
                />
              </Form.Item>
              <Form.Item label='Postal code' style={{ flex: 35 }}>
                <CustomInput
                  placeholder='Postal code'
                  height='36px'
                  value={thirdPartyPostalCode}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setThirdPartyPostalCode(e.target.value)
                  }
                />
              </Form.Item>
            </div>
            <div className={styles.sandboxBanner}>
              List rates will be displayed for third party account billing.
            </div>
          </>
        )}
      </section>

      <Divider style={{ margin: '16px 0' }} />

      <section className={styles.section}>
        <CustomTitle
          text='Shipment details'
          fontSize='18px'
          fontWeight='700'
          lineHeight='24px'
          marginBottom={16}
        />
        <Form.Item
          label={`${themeT('reference', theme.themeType)}${suffix}`}
          name='reference'
          rules={[
            {
              required: true,
              whitespace: true,
              message: `${themeT('reference', theme.themeType)}${suffix} is required.`,
            },
          ]}
        >
          <CustomInput
            maxLength={30}
            placeholder='PO # or reference ID or order #'
            height='36px'
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setReferenceValue(e.target.value)
            }
          />
        </Form.Item>
        {refStatus === 'checking' && (
          <div
            className={styles.refHint}
          >{`Checking ${themeT('reference', theme.themeType)}${suffix}…`}</div>
        )}
        {refStatus === 'shell' && !isReturnMode && (
          <div className={styles.refHintShell}>
            {`Tracking will be added to the existing shipment with matching
            ${themeT('reference', theme.themeType).toLowerCase()} #`}
          </div>
        )}
        {refStatus === 'booked' && !isReturnMode && (
          <div className={styles.refHintBooked}>
            {`Shipment already exists with tracking info for this ${themeT('reference', theme.themeType).toLowerCase()} #`}
          </div>
        )}
        {isReturnMode && (
          <div className={styles.refHint}>
            Creates a new return shipment soft-linked by this reference (RET-…).
            If that reference is already taken, it is auto-suffixed (RET-…-2,
            …).
          </div>
        )}
        {isReturnMode && returnRefBumpExhausted && (
          <div className={styles.refHintBooked}>
            Could not find a free RET- reference automatically. Edit the
            reference before quoting.
          </div>
        )}
        <Form.Item
          label='Ship date'
          name='shipDate'
          rules={[{ required: true, message: 'Ship date is required.' }]}
        >
          <CustomDatePicker
            placeholder='Enter date'
            style={{ width: '100%' }}
            dateFormat={shipDatePickerFormat}
          />
        </Form.Item>
        {showTeamsAndProducts && hasValidTeams && (
          <div className={styles.teamsField}>
            <div className={styles.teamsLabel}>
              {`${themeT('team', theme.themeType)}s (optional)`}
            </div>
            <TeamDropdownSelect
              teamsOptions={allTeams}
              selectedTeams={selectedTeams}
              onChange={setSelectedTeams}
              formState='add'
              onSearch={handleTeamsSearch}
              teamsSearch={teamsSearch}
            />
          </div>
        )}
      </section>

      <Divider style={{ margin: '16px 0' }} />

      <section className={styles.section}>
        <CustomTitle
          text='Pickup'
          fontSize='18px'
          fontWeight='700'
          lineHeight='24px'
          marginBottom={16}
        />
        <div className={styles.fieldGrid}>
          <Form.Item label='Company (optional)' name={['pickup', 'company']}>
            <CustomInput placeholder='Enter company name' height='36px' />
          </Form.Item>
          <Form.Item
            label='Contact name'
            name={['pickup', 'name']}
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Pickup contact name is required.',
              },
            ]}
          >
            <CustomInput placeholder='Enter contact name' height='36px' />
          </Form.Item>
        </div>
        <Form.Item
          label={
            pickupPhoneRequired ? 'Phone number' : 'Phone number (optional)'
          }
          name={['pickup', 'phone']}
          dependencies={['lithiumBatteries', 'provider']}
          rules={[
            {
              validator: async (_, v) => {
                const provider =
                  form.getFieldValue('provider') || selectedProvider;
                const lithiumOn = Boolean(
                  form.getFieldValue('lithiumBatteries')
                );
                if (!(provider === 'UPS' && lithiumOn)) return;
                const digits = phoneDigitsWithoutExtension(String(v ?? ''));
                if (digits.length < 10) {
                  throw new Error(
                    'Pickup phone is required for UPS lithium battery shipments (at least 10 digits).'
                  );
                }
              },
            },
          ]}
        >
          <CustomInput placeholder='Enter phone number' height='36px' />
        </Form.Item>
        <Form.Item
          label='Address'
          name={['pickup', 'street']}
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Pickup address is required.',
            },
          ]}
        >
          <CustomInput placeholder='Enter address' height='36px' />
        </Form.Item>
        <Form.Item
          label='Address line 2 (optional)'
          name={['pickup', 'street2']}
        >
          <CustomInput placeholder='Apt, suite, unit, etc.' height='36px' />
        </Form.Item>
        <div className={styles.fieldGrid}>
          <Form.Item
            label='City'
            name={['pickup', 'city']}
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'City is required.',
              },
            ]}
          >
            <CustomInput placeholder='Enter city' height='36px' />
          </Form.Item>
          <Form.Item
            label={`State / Province${isStateRequired(pickupCountry) ? '' : ' (optional)'}`}
            name={['pickup', 'state']}
            dependencies={[['pickup', 'country']]}
            rules={[
              ({ getFieldValue }) => ({
                required: isStateRequired(getFieldValue(['pickup', 'country'])),
                whitespace: true,
                message: 'State or province is required.',
              }),
              ({ getFieldValue }) => ({
                validator: async (_, value) => {
                  if (
                    value?.trim() &&
                    !isValidStateForCountry(
                      value,
                      getFieldValue(['pickup', 'country'])
                    )
                  ) {
                    throw new Error(
                      'Enter a valid state or province for the selected country.'
                    );
                  }
                },
              }),
            ]}
          >
            {pickupStateOptions ? (
              <CustomSelect
                placeholder='Select state / province'
                height='36px'
                showSearch={{ optionFilterProp: 'label' }}
                options={pickupStateOptions}
              />
            ) : (
              <CustomInput placeholder='Enter State / Province' height='36px' />
            )}
          </Form.Item>
          <Form.Item
            label='Postal code'
            name={['pickup', 'postalCode']}
            normalize={(v) =>
              normalizePostalCode(
                v,
                form.getFieldValue(['pickup', 'country'])
              ) ?? v
            }
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Postal code is required.',
              },
            ]}
          >
            <CustomInput placeholder='Enter postal code' height='36px' />
          </Form.Item>
          <Form.Item
            label='Country'
            name={['pickup', 'country']}
            rules={[{ required: true, message: 'Country is required.' }]}
          >
            <CustomSelect
              placeholder='Select'
              width='100%'
              height='36px'
              options={COUNTRY_OPTIONS}
              onChange={() =>
                form.setFieldValue(['pickup', 'state'], undefined)
              }
            />
          </Form.Item>
        </div>
        <Form.Item
          name={['pickup', 'residential']}
          valuePropName='checked'
          style={{ marginBottom: 0 }}
        >
          <Checkbox>Residential address</Checkbox>
        </Form.Item>
      </section>

      <Divider style={{ margin: '16px 0' }} />

      <section className={styles.section}>
        <CustomTitle
          text='Delivery'
          fontSize='18px'
          fontWeight='700'
          lineHeight='24px'
          marginBottom={16}
        />
        <div className={styles.fieldGrid}>
          <Form.Item label='Company (optional)' name={['delivery', 'company']}>
            <CustomInput placeholder='Enter company name' height='36px' />
          </Form.Item>
          <Form.Item
            label='Contact name'
            name={['delivery', 'name']}
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Delivery contact name is required.',
              },
            ]}
          >
            <CustomInput placeholder='Enter contact name' height='36px' />
          </Form.Item>
        </div>
        <Form.Item label='Phone number (optional)' name={['delivery', 'phone']}>
          <CustomInput placeholder='Enter phone number' height='36px' />
        </Form.Item>
        <Form.Item
          label='Address'
          name={['delivery', 'street']}
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Delivery address is required.',
            },
          ]}
        >
          <CustomInput placeholder='Enter address' height='36px' />
        </Form.Item>
        <Form.Item
          label='Address line 2 (optional)'
          name={['delivery', 'street2']}
        >
          <CustomInput placeholder='Apt, suite, unit, etc.' height='36px' />
        </Form.Item>
        <div className={styles.fieldGrid}>
          <Form.Item
            label='City'
            name={['delivery', 'city']}
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'City is required.',
              },
            ]}
          >
            <CustomInput placeholder='Enter city' height='36px' />
          </Form.Item>
          <Form.Item
            label={`State / Province${isStateRequired(deliveryCountry) ? '' : ' (optional)'}`}
            name={['delivery', 'state']}
            dependencies={[['delivery', 'country']]}
            rules={[
              ({ getFieldValue }) => ({
                required: isStateRequired(
                  getFieldValue(['delivery', 'country'])
                ),
                whitespace: true,
                message: 'State or province is required.',
              }),
              ({ getFieldValue }) => ({
                validator: async (_, value) => {
                  if (
                    value?.trim() &&
                    !isValidStateForCountry(
                      value,
                      getFieldValue(['delivery', 'country'])
                    )
                  ) {
                    throw new Error(
                      'Enter a valid state or province for the selected country.'
                    );
                  }
                },
              }),
            ]}
          >
            {deliveryStateOptions ? (
              <CustomSelect
                placeholder='Select state / province'
                height='36px'
                showSearch={{ optionFilterProp: 'label' }}
                options={deliveryStateOptions}
              />
            ) : (
              <CustomInput placeholder='Enter State / Province' height='36px' />
            )}
          </Form.Item>
          <Form.Item
            label='Postal code'
            name={['delivery', 'postalCode']}
            normalize={(v) =>
              normalizePostalCode(
                v,
                form.getFieldValue(['delivery', 'country'])
              ) ?? v
            }
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Postal code is required.',
              },
            ]}
          >
            <CustomInput placeholder='Enter postal code' height='36px' />
          </Form.Item>
          <Form.Item
            label='Country'
            name={['delivery', 'country']}
            rules={[{ required: true, message: 'Country is required.' }]}
          >
            <CustomSelect
              placeholder='Select'
              width='100%'
              height='36px'
              options={COUNTRY_OPTIONS}
              onChange={() =>
                form.setFieldValue(['delivery', 'state'], undefined)
              }
            />
          </Form.Item>
        </div>
        <Form.Item
          name={['delivery', 'residential']}
          valuePropName='checked'
          style={{ marginBottom: 0 }}
        >
          <Checkbox>Residential address</Checkbox>
        </Form.Item>
      </section>

      <Divider style={{ margin: '16px 0' }} />

      <section className={styles.section}>
        <CustomTitle
          text='Package details'
          fontSize='18px'
          fontWeight='700'
          lineHeight='24px'
          marginBottom={16}
        />
        <PackageLinesFields
          ref={linesRef}
          form={form}
          multiBoxEnabled={multiBoxEnabled}
          isReturnMode={isReturnMode}
          submitAttempted={submitAttempted}
          products={lineProductsSlot}
        />
        <div className={styles.packageOptions}>
          <CustomTitle
            level={4}
            text={
              manyBoxes
                ? `Special handling, applies to all ${totalBoxes(lines)} boxes`
                : 'Special handling'
            }
            fontSize='14px'
            fontWeight='600'
            lineHeight='16px'
            marginTop={0}
            marginBottom={8}
          />
          <div className={styles.packageOption}>
            <Form.Item name='dryIce' valuePropName='checked'>
              <Checkbox
                onChange={(e) => {
                  if (!e.target.checked) {
                    form.setFieldsValue({ dryIceKg: undefined });
                  }
                }}
              >
                Dry ice
              </Checkbox>
            </Form.Item>
            <p className={styles.packageOptionHint}>
              UN1845 packed as a refrigerant. Enter net weight in kilograms.
            </p>
            <Form.Item
              noStyle
              shouldUpdate={(prev, next) => prev.dryIce !== next.dryIce}
            >
              {({ getFieldValue }) =>
                getFieldValue('dryIce') ? (
                  <Form.Item
                    label={
                      manyBoxes ? 'Dry ice per box (kg)' : 'Dry ice weight (kg)'
                    }
                    name='dryIceKg'
                    rules={[
                      {
                        validator: async (_, v) => {
                          const kg = positiveFiniteNumber(v);
                          if (kg == null) {
                            throw new Error(
                              'Enter a dry ice weight greater than 0 kg.'
                            );
                          }
                          const weightLb = minBoxWeightLb(
                            form.getFieldsValue(true)
                          );
                          const pkgKg =
                            weightLb == null
                              ? undefined
                              : weightLb * 0.45359237;
                          if (pkgKg == null || kg >= pkgKg) {
                            const minLb = (kg / 0.45359237).toFixed(1);
                            throw new Error(
                              `Package weight must be greater than dry ice (${kg} kg is about ${minLb} lb).`
                            );
                          }
                        },
                      },
                    ]}
                    dependencies={LINE_WEIGHT_DEPENDENCIES}
                  >
                    <CustomInput placeholder='Enter amount' height='36px' />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
          </div>
          <div className={styles.packageOption}>
            <Form.Item name='nonStandardPackaging' valuePropName='checked'>
              <Checkbox>Non-standard packaging</Checkbox>
            </Form.Item>
            <p className={styles.packageOptionHint}>
              Package is in a non-traditional or irregularly shaped container.
            </p>
          </div>
          <div className={styles.packageOption}>
            <Form.Item name='lithiumBatteries' valuePropName='checked'>
              <Checkbox
                onChange={(e) => {
                  if (!e.target.checked) {
                    form.setFieldsValue({
                      lithiumBatteryMaterial: undefined,
                    });
                  }
                }}
              >
                Lithium batteries
              </Checkbox>
            </Form.Item>
            <p className={styles.packageOptionHint}>
              {selectedProvider === 'UPS'
                ? 'Contained in equipment (Section II / no Class 9 label). Pickup phone is required, and UPS may reject the booking if the account is not HazMat-enabled.'
                : 'Contained in equipment (Section II / no Class 9 label).'}
            </p>
            <Form.Item
              noStyle
              shouldUpdate={(prev, next) =>
                prev.lithiumBatteries !== next.lithiumBatteries
              }
            >
              {({ getFieldValue }) =>
                getFieldValue('lithiumBatteries') ? (
                  <Form.Item
                    label='Battery type'
                    name='lithiumBatteryMaterial'
                    rules={[
                      {
                        required: true,
                        message: 'Select lithium ion, metal, or both.',
                      },
                    ]}
                  >
                    <CustomSelect
                      placeholder='Select battery type'
                      width='100%'
                      height='36px'
                      options={[
                        { value: 'LITHIUM_ION', label: 'Lithium ion' },
                        { value: 'LITHIUM_METAL', label: 'Lithium metal' },
                        { value: 'BOTH', label: 'Both ion and metal' },
                      ]}
                    />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
          </div>
        </div>
        {multiBoxEnabled && <PackageTotals form={form} />}
      </section>

      <Divider style={{ margin: '16px 0' }} />

      <section className={styles.section}>
        <Form.Item label='Comments (optional)' name='comments'>
          <CustomInput
            placeholder='Notes, lot #, special instructions…'
            height='36px'
          />
        </Form.Item>
      </section>
    </Form>
  );

  const renderRates = (): React.ReactNode => (
    <div>
      <Button
        type='link'
        onClick={() => setStep('form')}
        icon={<ArrowLeftOutlined />}
        style={{ paddingLeft: 0, color: 'var(--text-primary)' }}
      >
        Back
      </Button>
      <h3 className={styles.title}>Available services</h3>
      {quoteWarnings.length > 0 && (
        <div className={styles.warningBanner} role='status'>
          {quoteWarnings.join('\n')}
        </div>
      )}
      <div className={styles.rateList}>
        {rates.map((rate, index) => {
          const rateKey =
            rate.rateId ||
            `${rate.serviceType}|${rate.deliveryDate ?? ''}|${rate.totalNetCharge ?? ''}|${index}`;
          const selected = selectedRate?.rateId === rateKey;
          return (
            <button
              type='button'
              key={rateKey}
              className={`${styles.rateCard} ${
                selected ? styles.rateCardSelected : ''
              }`}
              onClick={() => {
                setSelectedRate({ ...rate, rateId: rateKey });
                setBookingFailure(null);
              }}
            >
              <div className={styles.rateCardLeft}>
                <div className={styles.rateName}>
                  {rate.serviceDescription || prettyService(rate.serviceType)}
                </div>
                <div className={styles.rateSub}>
                  {formatRateArrival(rate) || (
                    <span className={styles.muted}>
                      Transit time unavailable
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.rateCardRight}>
                <div className={styles.ratePrice}>
                  {formatPrice(rate.totalNetCharge, rate.currency)}
                </div>
                <div className={styles.rateCurrency}>{rate.currency || ''}</div>
                {lastQuotePayload?.pieces && (
                  <div className={styles.rateBoxes}>
                    {lastQuotePayload.pieces.length} boxes,{' '}
                    {totalWeightLb(lastQuotePayload.pieces)} lb total
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  const metaItem = (label: string, value: React.ReactNode): React.ReactNode => (
    <div className={styles.resultMetaItem}>
      <span className={styles.resultMetaLabel}>{label}</span>
      <span className={styles.resultMetaValue}>{value}</span>
    </div>
  );

  const renderResultMeta = (
    trackingLabel: string,
    tracking: string
  ): React.ReactNode => (
    <div className={styles.resultMeta}>
      {metaItem(trackingLabel, tracking)}
      {metaItem(
        'Service',
        selectedRate?.serviceDescription ||
          prettyService(bookingResult?.serviceType)
      )}
      {metaItem(
        'Estimated delivery',
        bookingResult ? formatEta(bookingResult, selectedRate) : '—'
      )}
      {metaItem(
        'Ship date',
        bookingResult?.shipDate || lastQuotePayload?.shipDate || '—'
      )}
    </div>
  );

  const renderSandboxBanner = (extraClass = ''): React.ReactNode =>
    bookingResult?.isVirtualResponse && (
      <div className={`${styles.sandboxBanner} ${extraClass}`}>
        <strong>Sandbox / CIE response.</strong> The {providerLabel} test
        environment booked the shipment; label retrieval may be limited.
      </div>
    );

  const renderZebraStatus = (errorText: string): React.ReactNode =>
    serialPort &&
    zebraPrintStatus !== 'idle' && (
      <div className={styles.zplStatus}>
        {zebraPrintStatus === 'printing' && (
          <span className={styles.zplStatusSending}>
            ⏳ Sending to Zebra printer…
          </span>
        )}
        {zebraPrintStatus === 'sent' && (
          <span className={styles.zplStatusOk}>✓ Sent to Zebra printer</span>
        )}
        {zebraPrintStatus === 'error' && (
          <span className={styles.zplStatusErr}>{errorText}</span>
        )}
      </div>
    );

  const renderEmailRow = (
    buttonText: string,
    disabled: boolean
  ): React.ReactNode => (
    <div className={styles.emailRow}>
      <CustomInput
        placeholder='name@example.com'
        height='36px'
        value={emailTo}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setEmailTo(e.target.value);
          setEmailStatus('idle');
        }}
        style={{ flex: 1 }}
      />
      <Button
        type='primary'
        onClick={() => {
          const _ = handleEmailLabel();
        }}
        disabled={disabled || emailStatus === 'sending'}
        loading={emailStatus === 'sending'}
      >
        {buttonText}
      </Button>
    </div>
  );

  const renderMultiResult = (): React.ReactNode => {
    if (!bookingResult) return null;
    const countWarning = labelCountWarning(
      bookedBoxCount,
      multiLabels.length,
      providerLabel
    );
    return (
      <div>
        <h3 className={styles.title}>Labels generated</h3>
        {renderResultMeta(
          'Master tracking number',
          bookingResult.masterTrackingNumber ||
            bookingResult.trackingNumber ||
            '—'
        )}
        {countWarning && (
          <div className={`${styles.warningBanner} ${styles.labelCardList}`}>
            {countWarning}
          </div>
        )}
        {renderSandboxBanner(styles.labelCardList)}
        {renderZebraStatus('✗ Printer error — use Download on each box')}
        <div className={styles.labelCardList}>
          <LabelCards
            labels={multiLabels}
            boxCount={bookedBoxCount}
            getPng={getLabelPng}
            onPrint={(label) => {
              const _ = printLabelSet([label]);
            }}
            onDownload={downloadLabelFile}
            printDisabled={zebraPrintStatus === 'printing'}
          />
        </div>
        <div className={styles.emailBlock}>
          <div className={`${styles.resultMetaLabel} ${styles.emailLabel}`}>
            Email all labels to…
          </div>
          {renderEmailRow('Email labels', false)}
          {emailStatus === 'sent' && (
            <div className={`${styles.refHintShell} ${styles.emailHint}`}>
              {multiLabels.length} labels emailed to {emailTo.trim()}.
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderResult = (): React.ReactNode => {
    if (!bookingResult) return null;
    if (bookedBoxCount > 1) return renderMultiResult();
    const mime = bookingResult.labelContentType || 'application/pdf';
    const isZpl = mime.includes('zpl');
    const src = bookingResult.labelBase64
      ? `data:${mime};base64,${bookingResult.labelBase64}`
      : (bookingResult.labelUrl ?? '');

    return (
      <div>
        <h3 className={styles.title}>Label generated</h3>
        {renderResultMeta(
          'Tracking number',
          bookingResult.trackingNumber || '—'
        )}
        {renderSandboxBanner()}
        {renderZebraStatus('✗ Printer error — use Download ZPL below')}
        <div className={styles.labelFrame}>
          {labelPreviewLoading && (
            <span className={styles.muted}>Loading label preview…</span>
          )}
          {!labelPreviewLoading && labelPreviewUrl && (
            <img alt='Shipping label' src={labelPreviewUrl} />
          )}
          {!labelPreviewLoading &&
            !labelPreviewUrl &&
            !isZpl &&
            src &&
            (mime.includes('pdf') ? (
              <iframe title='Shipping label' src={src} />
            ) : (
              <img alt='Shipping label' src={src} />
            ))}
          {!labelPreviewLoading && !labelPreviewUrl && (isZpl || !src) && (
            <span className={styles.muted}>
              Label preview unavailable — use Download ZPL below.
            </span>
          )}
        </div>
        <div className={styles.emailBlock}>
          <div className={`${styles.resultMetaLabel} ${styles.emailLabel}`}>
            Email this label to…
          </div>
          {renderEmailRow(
            'Send',
            !labelPngBase64 && !bookingResult?.labelBase64
          )}
          {emailStatus === 'sent' && (
            <div className={`${styles.refHintShell} ${styles.emailHint}`}>
              Label emailed to {emailTo.trim()}
              {!labelPngBase64
                ? ' (ZPL attachment — Labelary preview unavailable).'
                : '.'}
            </div>
          )}
          {!labelPngBase64 && bookingResult?.labelBase64 && (
            <div className={`${styles.muted} ${styles.emailHint}`}>
              PNG preview unavailable — email will attach the ZPL label file
              instead.
            </div>
          )}
          {!labelPngBase64 && !bookingResult?.labelBase64 && (
            <div className={`${styles.muted} ${styles.emailHint}`}>
              Email is available once a label is ready.
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderActions = (): React.ReactNode => {
    if (step === 'form') {
      return (
        <div className={styles.actionsBlock}>
          <button
            type='button'
            className={styles.primaryAction}
            onClick={handleGetQuote}
            disabled={
              !!loading ||
              refStatus === 'checking' ||
              (!isReturnMode && refStatus === 'booked') ||
              (isReturnMode &&
                (refStatus === 'booked' || refStatus === 'shell'))
            }
          >
            Get quote
          </button>
          <button type='button' className={styles.linkAction} onClick={onClose}>
            Cancel
          </button>
        </div>
      );
    }
    if (step === 'rates') {
      return (
        <BookActions
          canBook={!!selectedRate}
          pending={!!loading}
          failure={bookingFailure}
          onBook={(confirmDuplicate) => {
            const _ = handleBook(confirmDuplicate);
          }}
          onClose={onClose}
        />
      );
    }
    const hasLabel = !!bookingResult?.labelBase64 || !!bookingResult?.labelUrl;
    const hasZpl = !!bookingResult?.labelBase64;
    const saveRetry = saveFailed && (
      <button
        type='button'
        className={styles.secondaryAction}
        onClick={() => {
          const _ = handleSaveShipment();
        }}
        disabled={!!loading}
      >
        Save shipment
      </button>
    );
    const connectZebra = serialSupported && !serialPort && (
      <button
        type='button'
        className={styles.linkAction}
        onClick={() => {
          const _ = handleConnectZebra();
        }}
      >
        Connect Zebra printer
      </button>
    );
    if (bookedBoxCount > 1) {
      return (
        <div className={styles.actionsBlock}>
          <button
            type='button'
            className={styles.primaryAction}
            onClick={() => {
              const _ = printLabelSet(multiLabels);
            }}
            disabled={!multiLabels.length || zebraPrintStatus === 'printing'}
          >
            {zebraPrintStatus === 'printing' ? 'Sending…' : 'Print all labels'}
          </button>
          {serialPort && (
            <button
              type='button'
              className={styles.secondaryAction}
              onClick={() => {
                const _ = printLabelSet(multiLabels, true);
              }}
              disabled={!multiLabels.length}
            >
              Print all labels (PDF)
            </button>
          )}
          {saveRetry}
          {connectZebra}
          <button type='button' className={styles.linkAction} onClick={onClose}>
            Close
          </button>
        </div>
      );
    }
    return (
      <div className={styles.actionsBlock}>
        {saveRetry}
        {serialPort ? (
          <button
            type='button'
            className={styles.primaryAction}
            onClick={() => {
              const _ = handlePrintToZebra();
            }}
            disabled={!hasZpl || zebraPrintStatus === 'printing'}
          >
            {zebraPrintStatus === 'printing' ? 'Sending…' : 'Print to Zebra'}
          </button>
        ) : (
          <button
            type='button'
            className={styles.primaryAction}
            onClick={handleDownloadZpl}
            disabled={!hasZpl}
          >
            Download ZPL
          </button>
        )}
        <button
          type='button'
          className={styles.secondaryAction}
          onClick={handlePrintPdf}
          disabled={!hasLabel && !labelPreviewUrl}
        >
          Print label (PDF)
        </button>
        {serialPort && (
          <button
            type='button'
            className={styles.secondaryAction}
            onClick={handleDownloadZpl}
            disabled={!hasZpl}
          >
            Download ZPL
          </button>
        )}
        {connectZebra}
        <button type='button' className={styles.linkAction} onClick={onClose}>
          Close
        </button>
      </div>
    );
  };

  return (
    <Spin spinning={!!loading} tip={loading || ''}>
      <div className={styles.container}>
        {step === 'form' && renderForm()}
        {step === 'rates' && renderRates()}
        {step === 'result' && renderResult()}
        {error && (
          <div role='alert' className={styles.errorBanner}>
            {error}
          </div>
        )}
        {renderActions()}
      </div>
    </Spin>
  );
};

export default QuoteShipmentDrawer;
